class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.header = null;
    this.footer = null;
    this.content = null;
    this.footerInView = false;
    this.footerObserver = null;
    this.naturalTop = 0;
    this.naturalBottom = 0;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.footer = document.querySelector('[data-ref="footer"]');
    this.content = this.querySelector('.sticky-add-to-cart__content');

    if (!this.content) return;

    // #Purphyt - Debut: Desactivation du sticky dans les dialog modales
    // Le sticky add-to-cart ne doit jamais s'appliquer dans une quick-add modal.
    if (this.closest('dialog')) {
      this.dataset.active = 'false';
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
      this.style.minHeight = '0';
      return;
    }
    // #Purphyt - Fin: Desactivation du sticky dans les dialog modales

    // Réserve l'espace du sticky dans le flux pour éviter le changement de
    // hauteur du document quand le contenu passe en position: fixed. Sans cela,
    // le contenu sort du flux → la page raccourcit → le footer remonte dans le
    // viewport → le sticky se désactive → boucle infinie de clignotement.
    requestAnimationFrame(() => {
      this.style.minHeight = `${this.offsetHeight}px`;
      this.#measureNaturalPosition();
      this.#initObservers();
      this.#updateDisplay();

      window.addEventListener('scroll', this.handleScroll);
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('load', this.handleResize);
    });
  }

  disconnectedCallback() {
    this.footerObserver?.disconnect();

    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('load', this.handleResize);
  }

  handleScroll() {
    this.#updateDisplay();
  }

  handleResize() {
    this.#measureNaturalPosition();
    this.#updateDisplay();
  }

  #initObservers() {
    if (!this.footer) return;

    this.footerObserver = new IntersectionObserver(([entry]) => {
      this.footerInView = entry.isIntersecting;
      this.#updateDisplay();
    });

    this.footerObserver.observe(this.footer);
  }

  #measureNaturalPosition() {
    const wasActive = this.dataset.active === 'true';

    if (wasActive) {
      this.dataset.active = 'false';
      this.content.classList.remove('color-scheme-1');
    }

    const rect = this.getBoundingClientRect();
    const top = rect.top + window.scrollY;

    this.naturalTop = top;
    this.naturalBottom = top + rect.height;

    if (wasActive) {
      this.dataset.active = 'true';
      this.content.classList.add('color-scheme-1');
    }
  }

  #isNaturalPositionInViewport() {
    const headerOffset = this.#getHeaderOverlapHeight();
    const viewportTop = window.scrollY + headerOffset;
    const viewportBottom = window.scrollY + window.innerHeight;

    return this.naturalTop < viewportBottom && this.naturalBottom > viewportTop;
  }

  #getHeaderOverlapHeight() {
    if (!this.header) {
      return 65;
    }

    const rect = this.header.getBoundingClientRect();
    if (rect.bottom <= 0) {
      return 0;
    }

    const computedStyle = window.getComputedStyle(this.header);
    const isStickyHeader = computedStyle.position === 'sticky' || computedStyle.position === 'fixed';
    if (!isStickyHeader) {
      return 0;
    }

    return Math.max(0, Math.min(rect.bottom, rect.height));
  }

  #updateDisplay() {
    const isPrimaryAddToCartVisible = this.#isNaturalPositionInViewport();
    const shouldBeActive = !isPrimaryAddToCartVisible && !this.footerInView;
    const isActive = this.dataset.active === 'true';

    if (shouldBeActive === isActive) return;

    clearTimeout(this.closingTimeout);

    if (shouldBeActive) {
      delete this.dataset.closing;
      this.dataset.active = 'true';
      this.content.classList.add('color-scheme-1');
      return;
    }

    this.dataset.active = 'false';
    this.dataset.closing = 'true';

    this.closingTimeout = setTimeout(() => {
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
    }, 300);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
